import { Button } from "@/components/ui/baseUi/button"
import { Input } from "@/components/ui/baseUi/input"
import { Text } from "@/components/ui/baseUi/text"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { cn } from "@/modules/lib/utils"
import { FileUploadDocument } from "@/modules/ui/components/fileUpload/fileUploadDocument"
import { FileUploadDragZone } from "@/modules/ui/components/fileUpload/fileUploadDragZone"
import { AlertIconDark } from "@images/svgIcons/alertIcon"
import { DocumentIcon } from "@images/svgIcons/medias/documentIcon"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { useBoxWaiverForm } from "@modules/box/hooks/useBoxWaiverForm"
import { word } from "@modules/core/utils/i18n"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { Archive, Check, Download, LoaderCircle, PenIcon, XIcon } from "lucide-react"
import { useState } from "react"

type BoxSettingsUploadDocumentProps = {
  className?: string
} & React.ComponentProps<"div">

export function BoxSettingsUploadDocument({ className, ...props }: BoxSettingsUploadDocumentProps) {
  const { form, displayFileNameInput, setDisplayFileNameInput, isSubmitting } = useBoxWaiverForm()
  const { result: box } = useCurrentBox()
  const confirmPopup = useConfirmPopup()
  const canEditAgreementDocument = useBoxStaffPermission(BoxStaffPermissions.EDIT_GYM_INFORMATION)

  const [isUploading, setIsUploading] = useState<boolean>(false)

  const fileVersionAndDate = box?.waiver
    ? box.waiver.fileName.replace(box.waiver.originalFileName.replace(/\.pdf$/i, ""), "")
    : ""

  const confirmationModaleTitle = (
    <div className="flex w-full flex-col items-center gap-2">
      <AlertIconDark className="text-error" color="currentColor" />
      <Text fontStyle="TL">{word("box.form.agreementDocument.archive.modale.title")}</Text>
    </div>
  )

  const confirmationModaleContent = (
    <Text fontStyle="BM" className="text-center" style={{ whiteSpace: "pre-line" }}>
      {word("box.form.agreementDocument.archive.modale.content")}
    </Text>
  )

  const archiveDocument = () => {
    if (!canEditAgreementDocument) return
    const waiver = form.state.values.waiver
    if (!waiver) return

    if (waiver.url) {
      confirmPopup.show({
        title: confirmationModaleTitle,
        message: confirmationModaleContent,
        accept: async () => {
          form.setFieldValue("waiver", null)
          await form.handleSubmit()
        },
        footerProps: {
          cancelText: word("global.cancel"),
          confirmText: word("box.form.agreementDocument.archive.button"),
        },
      })
    } else {
      form.setFieldValue("waiver", null)
      void form.handleSubmit()
    }
  }

  const renameDocument = () => {
    if (!canEditAgreementDocument) return
    const waiver = form.state.values.waiver
    if (!waiver) return

    setDisplayFileNameInput(true)
    form.setFieldValue("waiver.filename", waiver.filename?.replace(/\.pdf$/i, "") ?? "")
  }

  const downloadDocument = () => {
    if (!canEditAgreementDocument) return
    if (box?.waiver?.url) {
      window.open(box.waiver.url, "_blank")
    }
  }

  const cancelDocumentUpload = () => {
    if (!canEditAgreementDocument) return
    if (box?.waiver) {
      setDisplayFileNameInput(false)
    } else if (form.state.values.waiver) {
      form.setFieldValue("waiver", null)
      setDisplayFileNameInput(false)
    }
  }

  const confirmRenameDocumentAndSubmitForm = () => {
    if (!canEditAgreementDocument) return
    const waiver = form.state.values.waiver
    if (!waiver?.filename) return
    if ((form.getFieldMeta("waiver.filename")?.errors ?? []).length > 0) return

    void form.handleSubmit()
  }

  return (
    <form.Subscribe selector={(state) => state.values.waiver}>
      {(waiver) => (
        <div className={cn(className)} {...props}>
          {displayFileNameInput ? (
            <div className="flex grow flex-col overflow-hidden rounded-3xl bg-neutral-100">
              <div className="flex flex-row items-center justify-between gap-4 px-4" style={{ height: 145 }}>
                <div className="flex flex-row items-center gap-4">
                  <DocumentIcon color="" />

                  <form.AppField name="waiver.filename">
                    {(field) => (
                      <InputContainer
                        name="waiver.filename"
                        label={word("box.form.agreementDocument.name.label")}
                        isRequiredInput
                        error={<field.Message />}
                      >
                        <div className="inline-flex flex-grow-1 flex-row items-center gap-2">
                          <Input
                            id="waiver.filename"
                            value={field.state.value ?? ""}
                            disabled={!canEditAgreementDocument}
                            className="w-80"
                            placeholder={word("box.form.agreementDocument.name.placeholder")}
                            onBlur={field.handleBlur}
                            onChange={(e) => {
                              field.handleChange(e.currentTarget.value.replace(/\.pdf$/i, ""))
                            }}
                            aria-invalid={field.state.meta.isTouched && !field.state.meta.isValid}
                          />
                          <Text fontStyle="BM" className="ml-1 text-center text-neutral-900">
                            {`${fileVersionAndDate}.pdf`}
                          </Text>
                        </div>
                      </InputContainer>
                    )}
                  </form.AppField>
                </div>

                <div className="flex flex-row items-center gap-3">
                  <Button
                    variant="outline"
                    size="iconMd"
                    onClick={cancelDocumentUpload}
                    disabled={isSubmitting || !canEditAgreementDocument}
                    isLoading={isSubmitting}
                    className="bg-transparent"
                  >
                    <XIcon width={24} height={24} />
                  </Button>

                  <Button
                    variant="primary"
                    size="iconMd"
                    onClick={confirmRenameDocumentAndSubmitForm}
                    disabled={isSubmitting || !canEditAgreementDocument}
                    isLoading={isSubmitting}
                  >
                    {isSubmitting ? <LoaderCircle className="animate-spin" /> : <Check width={24} height={24} />}
                  </Button>
                </div>
              </div>
            </div>
          ) : !!box?.waiver && waiver && !waiver.isUploading ? (
            <div className="flex grow flex-col overflow-hidden rounded-3xl border border-neutral-300">
              <div className="flex flex-row items-center justify-between gap-4 px-4" style={{ height: 145 }}>
                <div className="flex w-8/12 flex-row items-center gap-4">
                  <DocumentIcon color="" />

                  <div className="flex flex-col gap-2 overflow-hidden" style={{ wordBreak: "break-word" }}>
                    <Text fontStyle="LL" className="text-neutral-700">
                      {box.waiver.fileName}
                    </Text>
                  </div>
                </div>

                <div className="flex flex-col items-center justify-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={downloadDocument}
                    disabled={!box.waiver.url || !canEditAgreementDocument}
                    className="w-full"
                  >
                    <Download color="currentColor" width={16} height={16} />
                    {word("box.form.agreementDocument.download.button")}
                  </Button>

                  <Button
                    variant="primary"
                    size="sm"
                    onClick={renameDocument}
                    disabled={!box.waiver.url || !canEditAgreementDocument}
                    className="w-full"
                  >
                    <PenIcon className="size-3.5" color="currentColor" />
                    {word("box.form.agreementDocument.rename.button")}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={archiveDocument}
                    disabled={!box.waiver.url || !canEditAgreementDocument}
                    className="w-full bg-transparent"
                  >
                    <Archive color="currentColor" width={16} height={16} />
                    {word("box.form.agreementDocument.archive.button")}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-[145px] grow flex-col overflow-hidden rounded-3xl bg-neutral-100">
              <FileUploadDocument
                setDocument={(i) => {
                  form.setFieldValue("waiver", i)
                }}
                onStartUpload={() => {
                  setIsUploading(true)
                }}
                onEndUpload={() => {
                  setIsUploading(false)
                  setDisplayFileNameInput(true)
                  form.setFieldMeta("waiver.filename", (prev) => ({ ...prev, isTouched: true }))
                }}
                disabled={isUploading || !canEditAgreementDocument}
                acceptedTypes="application/pdf"
                className="h-full"
              >
                <FileUploadDragZone
                  title={word("box.form.agreementDocument.dropZone.title")}
                  description={word("box.form.agreementDocument.dropZone.description")}
                  isLoading={isUploading}
                  disabled={!canEditAgreementDocument}
                />
              </FileUploadDocument>
            </div>
          )}
        </div>
      )}
    </form.Subscribe>
  )
}
